<!-- SelectNoteGroup.vue -->
<!-- Espelho de SelectNoteUser.vue.
     Lista grupos derivados de noteStore.noteGroups — populado por extractUniqueGroups()
     a partir das notas já carregadas em allNotes (mesmo critério do filtro Por usuário). -->
<script setup>
import { defineProps, defineEmits, computed, ref, watch } from "vue";
import AnchoredPanel from "@/components/Generics/AnchoredPanel.vue";
import { useNoteStore } from "@/stores/useNoteStore";

const props = defineProps({
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(["apply-selection", "close"]);

const noteStore = useNoteStore();

// --- Estado local ---
const searchTerm = ref("");
const searchInputRef = ref(null);
const tempSelectedGroupIds = ref([]);

// Sincroniza seleção temporária ao abrir o modal
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      tempSelectedGroupIds.value = [...noteStore.selectedGroupIdsForFilter];
    } else {
      searchTerm.value = "";
    }
  },
  { immediate: true }
);

// --- Lista de grupos vem da store — sem chamada ao backend ---
const filteredGroups = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  const base = noteStore.noteGroups || [];
  if (!term) return base;
  return base.filter((g) => (g.name || "").toLowerCase().includes(term));
});

const groups = computed(() => noteStore.noteGroups || []);

const allSelected = computed({
  get: () =>
    groups.value.length > 0 &&
    groups.value.every(g => tempSelectedGroupIds.value.includes(g.id)),
  set: (val) => {
    tempSelectedGroupIds.value = val ? groups.value.map(g => g.id) : [];
  },
});

function toggleAll() {
  allSelected.value = !allSelected.value;
}

// --- Ações ---
function applySelection() {
  noteStore.selectedGroupIdsForFilter = [...tempSelectedGroupIds.value];
  emit("apply-selection", true);
}
</script>

<template>
  <AnchoredPanel
    :open="isOpen"
    placement="side"
    align="left"
    :gap="8"
    @close="emit('close')"
    @opened="searchInputRef?.focus()"
  >
    <div
      class="group-dropdown-container"
      role="dialog"
      aria-label="Filtrar por grupo"
      @click.stop
      @mousedown.stop
    >
      <!-- Header mínimo (espaçamento) -->
      <!-- Campo de busca -->
      <div class="search-section">
        <label for="search-group-dropdown" class="sr-only">Buscar grupo</label>
        <input
          id="search-group-dropdown"
          ref="searchInputRef"
          v-model="searchTerm"
          type="text"
          class="search-input"
          placeholder="Buscar grupos"
          autocomplete="off"
          @keydown.enter.prevent="applySelection"
        />
      </div>

      <!-- Lista de grupos -->
      <ul class="groups-list">
        <li v-if="groups.length > 0" class="group-item" @click="toggleAll">
          <div class="group-label">
            <div class="group-left">
              <span class="group-name" style="font-weight: 600;">Todos os grupos</span>
            </div>
            <input type="checkbox" class="checkbox-style-modal" v-model="allSelected" @click.stop />
          </div>
        </li>
        <li v-if="groups.length > 0" class="list-divider" role="separator"></li>

        <!-- Grupos derivados das notas carregadas (noteStore.noteGroups) -->
        <li
          v-for="group in filteredGroups"
          :key="group.id"
          class="group-item"
        >
          <label
            :for="`dropdown-group-${group.id}`"
            class="group-label"
          >
            <div class="group-left">
              <span class="group-icon">
                <i class="fas fa-users"></i>
              </span>
              <span class="group-name" :title="group.name">
                {{ group.name }}
              </span>
            </div>

            <input
              :id="`dropdown-group-${group.id}`"
              v-model="tempSelectedGroupIds"
              :value="group.id"
              type="checkbox"
              class="checkbox-style-modal"
            />
          </label>
        </li>

        <!-- Vazio -->
        <li v-if="filteredGroups.length === 0" class="no-group-item">
          Nenhum grupo encontrado.
        </li>
      </ul>

      <!-- Ações -->
      <div class="modal-actions">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          @click="applySelection"
        >
          Filtrar
        </button>
      </div>
    </div>
  </AnchoredPanel>
</template>

<style scoped>
/* === Espelho exato de SelectNoteUser.vue === */

.group-dropdown-container {
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-color);
  width: 220px;
  display: flex;
  flex-direction: column;
}

.search-section {
  padding: 10px 10px 8px;
}

/* A .search-input canônica reserva 2.5rem à esquerda para o ícone de lupa;
   aqui não há ícone, então o texto e o cursor começam junto à borda. */
.search-input {
  padding-left: 0.75rem;
}

/* Foco no mesmo desenho do campo de "Por título" (SelectFieldFilter): borda
   lilás com o anel fino azul, no lugar do anel largo da .search-input canônica. */
.search-input:focus {
  border-color: var(--brand-light);
  box-shadow: var(--focus-ring-field);
}

.groups-list {
  list-style: none;
  padding: 4px 0;
  margin: 0;
  font-size: 0.82rem;
  color: var(--text-dark);
  font-weight: 500;
  overflow-y: auto;
  max-height: 190px;
}

.group-item {
  padding: 4px 10px;
}

.group-item:hover {
  background-color: var(--surface-hover);
  color: var(--text-dark);
}
.group-item:active {
  transform: none;
}

.group-label {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  gap: 8px;
}

.group-left {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.group-icon {
  width: 22px;
  height: 22px;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--neutral-200);
  color: var(--text-light);
  font-size: 0.7rem;
  flex: 0 0 auto;
}

.group-name {
  display: inline-block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
  line-height: 1.15;
}

.list-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 4px 10px;
  list-style: none;
}

.no-group-item {
  padding: 8px 10px;
  text-align: center;
  color: #999;
  font-style: italic;
  font-size: 0.8rem;
}

/* Checkbox — idêntico ao SelectNoteUser.vue */
.checkbox-style-modal {
  width: 0.95rem;
  height: 0.95rem;
  border: 1px solid var(--text-subtle);
  border-radius: 3px;
  background-color: var(--surface);
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  flex-shrink: 0;
  margin: 0;
  transition: all 0.15s ease-in-out;
}

.checkbox-style-modal:hover {
  border-color: var(--brand-light);
}

.checkbox-style-modal:checked {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
}

.checkbox-style-modal:checked:hover {
  background-color: var(--brand-light-hover);
  border-color: var(--brand-light-hover);
}

.checkbox-style-modal:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Ações */
.modal-actions {
  padding: 8px 10px;
  border-top: 1px solid var(--border-color);
  margin: 6px 10px 0 10px;
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
}

.btn-sm {
  height: 26px;
  padding: 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
}

</style>